import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MDEditor from '@uiw/react-md-editor';
import { RouteMap, LocationPickerMap } from '../../components/common';
import { DESIGN_TOKENS } from '../../styles/theme';
import type { Permission } from '../../types';
import { getGoogleMapsRoutePlannerUrl } from '../../utils';
import type { ListingFormState, ListingLocationKey } from './listingFormState';
import {
  createRouteGeoJsonFromPlannerPoints,
  fetchRouteGeoJsonFromPlannerPoints,
} from './listingEditor';

export { useListingForm, validateListingFormData } from './listingFormState';
export type { ListingFormState } from './listingFormState';

export interface ListingFormProps {
  formData: ListingFormState;
  setFormData: React.Dispatch<React.SetStateAction<ListingFormState>>;
  handleFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleDescriptionChange: (value?: string) => void;
  handleSelectChange: (e: any) => void;
  handleLocationChange: (type: ListingLocationKey, field: string, value: string) => void;
  handleDateChange: (field: 'startDate' | 'endDate', value: string) => void;
  handlePermissionChange: (permission: Permission) => void;
  handleLocationSelect: (type: ListingLocationKey, lat: number, lng: number) => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  submitLabel: string;
  isEdit?: boolean;
}

const LISTING_PERMISSIONS: Permission[] = ['BF2', 'BF3', 'BF4', 'HIPO'];

function FormSectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function GeoJsonUploadSection({
  formData,
  isEdit,
  onFileUpload,
  onRemoveRoute,
}: {
  formData: ListingFormState;
  isEdit: boolean;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveRoute: () => void;
}) {
  const uploadId = isEdit ? 'geojson-upload-edit' : 'geojson-upload';

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: DESIGN_TOKENS.colors.primary }}>
        Trasa GeoJSON (opcjonalnie)
      </Typography>
      <Box
        sx={{
          p: 3,
          border: `2px dashed ${DESIGN_TOKENS.colors.border}`,
          borderRadius: 2,
          textAlign: 'center',
          backgroundColor: 'rgba(0,0,0,0.02)',
          mb: 2,
        }}
      >
        <input
          accept=".geojson,.json"
          style={{ display: 'none' }}
          id={uploadId}
          type="file"
          onChange={onFileUpload}
        />
        <label htmlFor={uploadId}>
          <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} sx={{ mb: 1 }}>
            Wgraj plik GeoJSON
          </Button>
        </label>
        <Typography variant="body2" color="text.secondary">
          Wybierz plik z trasą w formacie .geojson lub .json
        </Typography>
      </Box>

      {formData.route_geojson && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Podgląd trasy:
          </Typography>
          <RouteMap
            start={formData.position_start.lat ? { lat: parseFloat(formData.position_start.lat), lng: parseFloat(formData.position_start.lng) } : undefined}
            end={formData.end_position.lat ? { lat: parseFloat(formData.end_position.lat), lng: parseFloat(formData.end_position.lng) } : undefined}
            geojson={formData.route_geojson}
          />
          <Button size="small" color="error" onClick={onRemoveRoute} sx={{ mt: 1 }}>
            Usuń trasę
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ListingLocationFields({
  formData,
  locationType,
  title,
  onLocationChange,
  onLocationSelect,
}: {
  formData: ListingFormState;
  locationType: ListingLocationKey;
  title: string;
  onLocationChange: (type: ListingLocationKey, field: string, value: string) => void;
  onLocationSelect: (type: ListingLocationKey, lat: number, lng: number) => void;
}) {
  const location = formData[locationType];

  return (
    <Grid item xs={12} md={6}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: DESIGN_TOKENS.colors.primary }}>
        {title}
      </Typography>

      <LocationPickerMap
        lat={parseFloat(location.lat) || 0}
        lng={parseFloat(location.lng) || 0}
        onLocationSelect={(lat, lng) => onLocationSelect(locationType, lat, lng)}
      />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Miasto"
            size="small"
            value={location.city}
            onChange={(e) => onLocationChange(locationType, 'city', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Adres (opcjonalnie)"
            size="small"
            value={location.address}
            onChange={(e) => onLocationChange(locationType, 'address', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Szerokość (Lat)"
            size="small"
            value={location.lat}
            onChange={(e) => onLocationChange(locationType, 'lat', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Długość (Lng)"
            size="small"
            value={location.lng}
            onChange={(e) => onLocationChange(locationType, 'lng', e.target.value)}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

function ListingRequirementsSection({
  formData,
  handleDateChange,
  handlePermissionChange,
  handleSelectChange,
}: {
  formData: ListingFormState;
  handleDateChange: (field: 'startDate' | 'endDate', value: string) => void;
  handlePermissionChange: (permission: Permission) => void;
  handleSelectChange: (e: any) => void;
}) {
  return (
    <FormSectionCard title="Wymagania i termin">
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Data rozpoczęcia"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.time_frame.startDate}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Data zakończenia"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.time_frame.endDate}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Dostępność ogłoszenia</InputLabel>
            <Select
              name="Dedicated"
              value={formData.Dedicated}
              label="Dostępność ogłoszenia"
              onChange={handleSelectChange}
            >
              <MenuItem value="NONE">Dla wszystkich</MenuItem>
              <MenuItem value="WORKERS_ONLY">Tylko dla pracowników</MenuItem>
              <MenuItem value="FOR_VERIFIED_COOLLABOR">Tylko dla zweryfikowanych</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Wymagane uprawnienia
      </Typography>
      <FormGroup row>
        {LISTING_PERMISSIONS.map((permission) => (
          <FormControlLabel
            key={permission}
            control={<Checkbox checked={formData.permissions.includes(permission)} onChange={() => handlePermissionChange(permission)} />}
            label={permission}
          />
        ))}
      </FormGroup>
    </FormSectionCard>
  );
}

function RoutePlannerSection({
  formData,
  setFormData,
}: {
  formData: ListingFormState;
  setFormData: React.Dispatch<React.SetStateAction<ListingFormState>>;
}) {
  const [draggedPointIndex, setDraggedPointIndex] = React.useState<number | null>(null);
  const [isRouteGenerating, setIsRouteGenerating] = React.useState(false);
  const [routeGenerationError, setRouteGenerationError] = React.useState<string | null>(null);
  const [parkingPoints, setParkingPoints] = React.useState<Array<{
    id: string | number;
    lat: number;
    lng: number;
    name?: string;
    hgv?: string;
    operator?: string;
  }>>([]);

  React.useEffect(() => {
    let isCancelled = false;

    const loadParkingPoints = async () => {
      try {
        const response = await fetch('/truck_parking_poland.json');
        if (!response.ok) {
          throw new Error('Failed to load parking data');
        }

        const data = await response.json();
        if (isCancelled || !Array.isArray(data)) return;

        setParkingPoints(
          data
            .filter((item) => Number.isFinite(item?.lat) && Number.isFinite(item?.lon))
            .map((item) => ({
              id: item.id ?? `${item.lat}-${item.lon}`,
              lat: Number(item.lat),
              lng: Number(item.lon),
              name: item.tags?.['name:pl'] ?? item.tags?.name,
              hgv: item.tags?.hgv,
              operator: item.tags?.operator,
            }))
        );
      } catch {
        if (!isCancelled) {
          setParkingPoints([]);
        }
      }
    };

    loadParkingPoints();

    return () => {
      isCancelled = true;
    };
  }, []);

  const plannerMarkers = formData.route_points
    .map((point, index) => {
      const lat = Number.parseFloat(point.lat);
      const lng = Number.parseFloat(point.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

      return {
        lat,
        lng,
        label: `Punkt ${index + 1}: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        pointIndex: index,
      };
    })
    .filter((marker): marker is { lat: number; lng: number; label: string; pointIndex: number } => marker !== null);

  const plannerPolyline = plannerMarkers.map((marker) => [marker.lat, marker.lng] as [number, number]);
  const googleMapsPlannerUrl = getGoogleMapsRoutePlannerUrl(
    plannerMarkers.map((marker) => ({ lat: marker.lat, lng: marker.lng }))
  );

  const updateRoutePoints = (nextPoints: ListingFormState['route_points']) => {
    setFormData((previous) => ({
      ...previous,
      route_points: nextPoints,
    }));
  };

  const handleAddPointFromMap = (lat: number, lng: number) => {
    updateRoutePoints([
      ...formData.route_points,
      {
        lat: lat.toFixed(6),
        lng: lng.toFixed(6),
      },
    ]);
  };

  const handlePointChange = (index: number, field: 'lat' | 'lng', value: string) => {
    updateRoutePoints(
      formData.route_points.map((point, pointIndex) =>
        pointIndex === index ? { ...point, [field]: value } : point
      )
    );
  };

  const handleRemovePoint = (index: number) => {
    updateRoutePoints(formData.route_points.filter((_, pointIndex) => pointIndex !== index));
  };

  const handleClearPoints = () => {
    updateRoutePoints([]);
  };

  const handleMarkerMove = (index: number, lat: number, lng: number) => {
    updateRoutePoints(
      formData.route_points.map((point, pointIndex) =>
        pointIndex === index
          ? {
              ...point,
              lat: lat.toFixed(6),
              lng: lng.toFixed(6),
            }
          : point
      )
    );
  };

  const movePoint = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const nextPoints = [...formData.route_points];
    const [movedPoint] = nextPoints.splice(fromIndex, 1);
    nextPoints.splice(toIndex, 0, movedPoint);
    updateRoutePoints(nextPoints);
  };

  React.useEffect(() => {
    if (formData.route_points.length < 2) {
      setIsRouteGenerating(false);
      setRouteGenerationError(null);
      setFormData((previous) => ({
        ...previous,
        route_geojson: createRouteGeoJsonFromPlannerPoints(previous.route_points),
      }));
      return;
    }

    let isCancelled = false;
    setIsRouteGenerating(true);
    setRouteGenerationError(null);

    const timer = window.setTimeout(async () => {
      try {
        const generatedRoute = await fetchRouteGeoJsonFromPlannerPoints(formData.route_points);
        if (isCancelled) return;

        setFormData((previous) => ({
          ...previous,
          route_geojson: generatedRoute,
        }));
      } catch (error) {
        if (isCancelled) return;

        setRouteGenerationError('Nie udało się automatycznie wyznaczyć trasy. Zapisujemy przebieg po punktach.');
        setFormData((previous) => ({
          ...previous,
          route_geojson: createRouteGeoJsonFromPlannerPoints(previous.route_points),
        }));
      } finally {
        if (!isCancelled) {
          setIsRouteGenerating(false);
        }
      }
    }, 700);

    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [formData.route_points, setFormData]);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: DESIGN_TOKENS.colors.primary }}>
        Planer trasy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Klikaj na mapie, aby dodawać kolejne punkty trasy. Proponowany przebieg renderuje się od razu w planerze, a punkty możesz później przeciągać i modyfikować.
      </Typography>
      {isRouteGenerating ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Wyznaczam sugerowaną trasę pomiędzy punktami...
        </Alert>
      ) : null}
      {routeGenerationError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {routeGenerationError}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${DESIGN_TOKENS.colors.border}`,
              backgroundColor: DESIGN_TOKENS.colors.background,
              minHeight: 420,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Lista punktów
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {googleMapsPlannerUrl ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    href={googleMapsPlannerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Eksport do Google Maps
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    disabled
                  >
                    Eksport do Google Maps
                  </Button>
                )}
                <Button size="small" color="error" onClick={handleClearPoints} disabled={formData.route_points.length === 0}>
                  Wyczyść
                </Button>
              </Box>
            </Box>

            {formData.route_points.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nie dodano jeszcze punktów. Kliknij na mapie po prawej, aby zbudować trasę.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formData.route_points.map((point, index) => (
                  <Box
                    key={`route-point-${index}`}
                    draggable
                    onDragStart={() => setDraggedPointIndex(index)}
                    onDragEnd={() => setDraggedPointIndex(null)}
                    onDragOver={(event) => {
                      event.preventDefault();
                    }}
                    onDrop={() => {
                      if (draggedPointIndex === null) return;
                      movePoint(draggedPointIndex, index);
                      setDraggedPointIndex(null);
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                      backgroundColor: DESIGN_TOKENS.colors.surface,
                      cursor: 'grab',
                      opacity: draggedPointIndex === index ? 0.65 : 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <DragIndicatorIcon sx={{ color: DESIGN_TOKENS.colors.textSecondary, cursor: 'grab' }} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Punkt {index + 1}
                      </Typography>
                    </Box>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Szerokość (Lat)"
                          value={point.lat}
                          onChange={(event) => handlePointChange(index, 'lat', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Długość (Lng)"
                          value={point.lng}
                          onChange={(event) => handlePointChange(index, 'lng', event.target.value)}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                      <Button size="small" color="error" onClick={() => handleRemovePoint(index)}>
                        Usuń punkt
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} lg={7}>
          <LocationPickerMap
            lat={plannerMarkers[0]?.lat ?? 0}
            lng={plannerMarkers[0]?.lng ?? 0}
            onLocationSelect={handleAddPointFromMap}
            markers={plannerMarkers}
            parkingPoints={parkingPoints}
            polylinePositions={plannerPolyline}
            geojson={formData.route_geojson}
            height={420}
            onMarkerMove={handleMarkerMove}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Niebieska linia pokazuje sugerowaną trasę między kluczowymi punktami. Turkusowe punkty oznaczają parkingi z bazy `truck_parking_poland.json`.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

export function ListingForm({
  formData,
  setFormData,
  handleFieldChange,
  handleDescriptionChange,
  handleSelectChange,
  handleLocationChange,
  handleDateChange,
  handlePermissionChange,
  handleLocationSelect,
  handleFileUpload,
  handleSubmit,
  isSubmitting,
  submitLabel,
  isEdit = false,
}: ListingFormProps) {
  const navigate = useNavigate();

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormSectionCard title="Podstawowe informacje">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tytuł ogłoszenia"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleFieldChange}
                  placeholder="np. Pilotowanie transportu ponadgabarytowego na trasie Warszawa - Berlin"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                  Szczegóły dotyczące zlecenia *
                </Typography>
                <Box data-color-mode="light">
                  <MDEditor
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    preview="edit"
                    height={300}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Wartość zlecenia (PLN netto)"
                  name="order_value"
                  type="number"
                  value={formData.order_value}
                  onChange={handleFieldChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Liczba pilotów"
                  name="pilots_quantity"
                  type="number"
                  required
                  value={formData.pilots_quantity}
                  onChange={handleFieldChange}
                  inputProps={{ min: 1, step: 1 }}
                  helperText="Maksymalna liczba pilotów przypisanych do ogłoszenia."
                />
              </Grid>
              {isEdit && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status ogłoszenia</InputLabel>
                    <Select
                      name="listing_status"
                      value={formData.listing_status}
                      label="Status ogłoszenia"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="OPEN">Otwarte</MenuItem>
                      <MenuItem value="CLOSED">Zrealizowane</MenuItem>
                      <MenuItem value="CANCELED">Anulowane</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <GeoJsonUploadSection
              formData={formData}
              isEdit={isEdit}
              onFileUpload={handleFileUpload}
              onRemoveRoute={() => setFormData((prev) => ({ ...prev, route_geojson: null, route_points: [] }))}
            />

            <RoutePlannerSection
              formData={formData}
              setFormData={setFormData}
            />
          </FormSectionCard>
        </Grid>

        <Grid item xs={12}>
          <FormSectionCard title="Trasa">
            <Grid container spacing={4}>
              <ListingLocationFields
                formData={formData}
                locationType="position_start"
                title="Miejscowość początkowa"
                onLocationChange={handleLocationChange}
                onLocationSelect={handleLocationSelect}
              />
              <ListingLocationFields
                formData={formData}
                locationType="end_position"
                title="Miejscowość końcowa"
                onLocationChange={handleLocationChange}
                onLocationSelect={handleLocationSelect}
              />
            </Grid>
          </FormSectionCard>
        </Grid>

        <Grid item xs={12}>
          <ListingRequirementsSection
            formData={formData}
            handleDateChange={handleDateChange}
            handlePermissionChange={handlePermissionChange}
            handleSelectChange={handleSelectChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/account/my-listings')} disabled={isSubmitting}>
              Anuluj
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSubmitting ? 'Przetwarzanie...' : submitLabel}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
